import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TableChartIcon from '@mui/icons-material/TableChart';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  CustomButton,
  DialogBody,
  DialogContainer,
  DialogTitle,
  LabelContent,
  LoadingSkeleton,
} from '../../../common/components';
import { useHistoricalAction } from '../../../hooks/useHistoricalActions';
import {
  formatAuditDescription,
  formatAuditTimestamp,
  formatAuditUser,
  formatHttpMethod,
  formatRequestPath,
} from '../utils/formatters';
import {
  parseAuditDetails,
  isPlainDetailObject,
  stringifyAuditDetailValue,
  toDetailObjectEntries,
} from '../utils/detailsParser';

const DetailObjectGrid = ({ value, resolveReference }) => {
  const entries = toDetailObjectEntries(value, { resolveReference });
  if (!entries.length) return null;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: 1.5,
      }}
    >
      {entries.map((entry) => (
        <Box
          key={entry.key}
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            p: 1.25,
            backgroundColor: (theme) => theme.palette.grey[50],
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {entry.label}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontFamily: entry.isCodeValue ? 'monospace' : 'inherit',
              fontSize: entry.isCodeValue ? '0.8rem' : '0.9rem',
              lineHeight: 1.35,
            }}
            title={entry.isCodeValue ? entry.value : undefined}
          >
            {entry.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const DetailCard = ({ title, value, resolveReference }) => (
  <Paper
    elevation={0}
    sx={{
      border: (theme) => `1px solid ${theme.palette.divider}`,
      borderRadius: 1.5,
      p: 2,
      backgroundColor: 'background.paper',
    }}
  >
    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
      {title}
    </Typography>
    {isPlainDetailObject(value) ? (
      <DetailObjectGrid value={value} resolveReference={resolveReference} />
    ) : (
      <Typography
        component="pre"
        sx={{
          m: 0,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontFamily: Array.isArray(value) ? 'monospace' : 'inherit',
          fontSize: '0.9rem',
          lineHeight: 1.4,
        }}
      >
        {stringifyAuditDetailValue(value)}
      </Typography>
    )}
  </Paper>
);

const AuditChangesTable = ({ changes = [], emptyMessage }) => {
  if (!changes.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyMessage || 'No se detectaron cambios de campos para esta acción.'}
      </Typography>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 1.5,
        overflow: 'hidden',
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Campo</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Antes</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Después</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {changes.map((change) => (
            <TableRow key={change.key}>
              <TableCell sx={{ verticalAlign: 'top' }}>{change.label}</TableCell>
              <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {change.before}
              </TableCell>
              <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {change.after}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
};

const AuditDetailsDialog = ({
  open,
  onClose,
  action,
  resolveReference,
  onExportAction,
  exportLoading = false,
}) => {
  const actionId = action?.id;

  const { data: actionDetails, isLoading: actionDetailsLoading } = useHistoricalAction(actionId, {
    enabled: Boolean(open && actionId),
  });

  if (!action) return null;

  const actionData = actionDetails || action;

  const { changes, changeSummary, entityReference, additionalDetails } = parseAuditDetails(actionData, {
    resolveReference,
  });

  const summaryFields = [
    { label: 'Fecha y hora', value: formatAuditTimestamp(actionData.created_at) || '—' },
    { label: 'Usuario', value: formatAuditUser(actionData) },
    { label: 'Módulo', value: actionData.module?.display_name || actionData.module?.key || '—' },
    { label: 'Acción', value: formatAuditDescription(actionData) },
  ];

  const technicalFields = [
    { label: 'Método HTTP', value: formatHttpMethod(actionData.http_method) || '—' },
    { label: 'Endpoint', value: formatRequestPath(actionData.request_path, actionData.endpoint) || '—' },
    { label: 'Código de estado', value: actionData.status_code ?? '—' },
    { label: 'Dirección IP', value: actionData.remote_addr || '—' },
    { label: 'User Agent', value: actionData.user_agent || '—' },
  ];

  return (
    <DialogContainer open={open} onClose={onClose} size="large" minHeight="auto">
      <DialogTitle>Detalle de acción auditada</DialogTitle>

      <DialogBody sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {actionDetailsLoading && !actionDetails ? <LoadingSkeleton /> : null}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          {summaryFields.map((field) => (
            <LabelContent
              key={field.label}
              label={field.label}
              content={field.value === null || field.value === undefined || field.value === '' ? '—' : field.value}
            />
          ))}
        </Box>

        {entityReference?.display ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" color="text.secondary">
              Registro afectado:
            </Typography>
            <Chip label={entityReference.display} size="small" color="primary" variant="outlined" />
          </Box>
        ) : null}

        <Divider />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="h6">Cambios realizados</Typography>
          <AuditChangesTable
            changes={changes}
            emptyMessage={changeSummary?.message}
          />
          {typeof changeSummary?.count === 'number' ? (
            <Typography variant="caption" color="text.secondary">
              Campos modificados: {changeSummary.count}
            </Typography>
          ) : null}
        </Box>

        <Divider />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="h6">Detalles adicionales</Typography>
          {additionalDetails.length > 0 ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1.5 }}>
              {additionalDetails.map((detail) => (
                <DetailCard
                  key={detail.id}
                  title={detail.label}
                  value={detail.value}
                  resolveReference={resolveReference}
                />
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No se registraron detalles adicionales para esta acción.
            </Typography>
          )}
        </Box>

        <Accordion disableGutters elevation={0} sx={{ border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 1.5, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight={600}>
              Información técnica
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              {technicalFields.map((field) => (
                <LabelContent
                  key={field.label}
                  label={field.label}
                  content={field.value}
                />
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      </DialogBody>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
          p: 2,
          pt: 0,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <CustomButton
            variant="info"
            buttonIcon={TableChartIcon}
            onClick={() => onExportAction?.(actionData, 'xlsx')}
            isLoading={exportLoading}
            disabled={actionDetailsLoading}
          >
            Descargar XLSX
          </CustomButton>
          <CustomButton
            variant="info"
            buttonIcon={PictureAsPdfIcon}
            onClick={() => onExportAction?.(actionData, 'pdf')}
            isLoading={exportLoading}
            disabled={actionDetailsLoading}
          >
            Descargar PDF
          </CustomButton>
        </Box>
        <CustomButton onClick={onClose} variant="base">
          Cerrar
        </CustomButton>
      </Box>
    </DialogContainer>
  );
};

export default AuditDetailsDialog;
